import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import { Star, DollarSign, Wallet, TrendingUp, Clock, Landmark, Save } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CaregiverProfile() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('earnings');
  const [earnings, setEarnings] = useState(null);
  const [ratings, setRatings] = useState(null);
  const [bankDetails, setBankDetails] = useState({ account_name: '', sort_code: '', account_number: '', bank_name: '' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEarnings();
    fetchRatings();
    fetchBankDetails();
  }, []);

  const fetchEarnings = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/earnings`, { withCredentials: true });
      setEarnings(data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const fetchRatings = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/ratings/my`, { withCredentials: true });
      setRatings(data);
    } catch (err) { console.error(err); }
  };

  const fetchBankDetails = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/bank-details`, { withCredentials: true });
      setBankDetails(data);
    } catch (err) { console.error(err); }
  };

  const saveBankDetails = async () => {
    try {
      await axios.put(`${API_URL}/api/bank-details`, bankDetails, { withCredentials: true });
      toast.success('Bank details saved');
    } catch (err) { toast.error('Failed to save'); }
  };

  const tabs = [
    { id: 'earnings', label: 'Earnings', icon: DollarSign },
    { id: 'ratings', label: 'Ratings', icon: Star },
    { id: 'bank', label: 'Bank Details', icon: Landmark },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="caregiver-profile-page">
        <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground">
          <Wallet className="w-7 h-7 inline mr-3 text-[#00A6A0]" />My Profile
        </h1>

        <div className="flex gap-2 border-b border-border pb-2">
          {tabs.map(t => (
            <Button key={t.id} variant="ghost" size="sm"
              className={activeTab === t.id ? 'text-[#00A6A0] border-b-2 border-[#00A6A0] rounded-none' : 'text-muted-foreground'}
              onClick={() => setActiveTab(t.id)} data-testid={`tab-${t.id}`}>
              <t.icon className="w-4 h-4 mr-1.5" />{t.label}
            </Button>
          ))}
        </div>

        {activeTab === 'earnings' && earnings && (
          <div className="space-y-4">
            <div className="grid sm:grid-cols-3 gap-4">
              <Card className="glass-card border-border"><CardContent className="p-5">
                <p className="text-xs text-muted-foreground">This Month</p>
                <p className="text-2xl font-bold text-[#00A6A0]">${earnings.current_period?.total_earnings?.toFixed(2)}</p>
                <p className="text-xs text-muted-foreground">{earnings.current_period?.total_hours?.toFixed(1)}h / {earnings.current_period?.shift_count} shifts</p>
              </CardContent></Card>
              <Card className="glass-card border-border"><CardContent className="p-5">
                <p className="text-xs text-muted-foreground">Year to Date</p>
                <p className="text-2xl font-bold text-[#FF6F61]">${earnings.year_to_date?.total_earnings?.toFixed(2)}</p>
                <p className="text-xs text-muted-foreground">{earnings.year_to_date?.total_hours?.toFixed(1)}h / {earnings.year_to_date?.shift_count} shifts</p>
              </CardContent></Card>
              <Card className="glass-card border-border"><CardContent className="p-5">
                <p className="text-xs text-muted-foreground">Avg Hourly</p>
                <p className="text-2xl font-bold text-[#A8E6CF]">${earnings.current_period?.total_hours > 0 ? (earnings.current_period.total_earnings / earnings.current_period.total_hours).toFixed(2) : '0.00'}</p>
              </CardContent></Card>
            </div>
            {earnings.recent_payslips?.length > 0 && (
              <Card className="glass-card border-border">
                <CardHeader><CardTitle className="text-sm text-foreground">Recent Payslips</CardTitle></CardHeader>
                <CardContent className="p-0"><div className="divide-y divide-border">
                  {earnings.recent_payslips.map(p => (
                    <div key={p.id} className="px-5 py-3 flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{p.period}</span>
                      <span className="text-sm font-medium text-[#00A6A0]">${p.total_pay?.toFixed(2)}</span>
                    </div>
                  ))}
                </div></CardContent>
              </Card>
            )}
          </div>
        )}

        {activeTab === 'ratings' && (
          <div className="space-y-4">
            <div className="flex items-center gap-4 p-6 bg-card border border-border rounded-xl">
              <div className="text-center">
                <p className="text-4xl font-bold text-amber-400">{ratings?.avg_rating || '0.0'}</p>
                <div className="flex gap-0.5 justify-center mt-1">
                  {[1,2,3,4,5].map(i => <Star key={i} className={`w-4 h-4 ${i <= (ratings?.avg_rating || 0) ? 'text-amber-400 fill-amber-400' : 'text-muted-foreground'}`} />)}
                </div>
                <p className="text-xs text-muted-foreground mt-1">{ratings?.total_ratings || 0} reviews</p>
              </div>
            </div>
            {ratings?.ratings?.map(r => (
              <Card key={r.id} className="glass-card border-border">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="flex gap-0.5">{[1,2,3,4,5].map(i => <Star key={i} className={`w-3 h-3 ${i <= r.rating ? 'text-amber-400 fill-amber-400' : 'text-muted-foreground'}`} />)}</div>
                    <span className="text-xs text-muted-foreground">by {r.rated_by_name}</span>
                  </div>
                  {r.comment && <p className="text-sm text-muted-foreground">{r.comment}</p>}
                  <p className="text-[10px] text-muted-foreground mt-1">{new Date(r.created_at).toLocaleDateString()}</p>
                </CardContent>
              </Card>
            ))}
            {(!ratings?.ratings || ratings.ratings.length === 0) && <p className="text-center text-muted-foreground py-8">No ratings yet</p>}
          </div>
        )}

        {activeTab === 'bank' && (
          <Card className="glass-card border-border">
            <CardHeader><CardTitle className="text-lg text-foreground">Bank Details</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className="text-xs text-muted-foreground">Account Name</label>
                  <Input value={bankDetails.account_name} onChange={e => setBankDetails(p => ({...p, account_name: e.target.value}))} className="bg-muted border-border text-foreground" data-testid="bank-account-name" /></div>
                <div><label className="text-xs text-muted-foreground">Bank Name</label>
                  <Input value={bankDetails.bank_name} onChange={e => setBankDetails(p => ({...p, bank_name: e.target.value}))} className="bg-muted border-border text-foreground" data-testid="bank-name" /></div>
                <div><label className="text-xs text-muted-foreground">Sort Code</label>
                  <Input value={bankDetails.sort_code} onChange={e => setBankDetails(p => ({...p, sort_code: e.target.value}))} placeholder="XX-XX-XX" className="bg-muted border-border text-foreground" data-testid="bank-sort-code" /></div>
                <div><label className="text-xs text-muted-foreground">Account Number</label>
                  <Input value={bankDetails.account_number} onChange={e => setBankDetails(p => ({...p, account_number: e.target.value}))} className="bg-muted border-border text-foreground" data-testid="bank-account-number" /></div>
              </div>
              <Button className="btn-primary" onClick={saveBankDetails} data-testid="save-bank-btn"><Save className="w-4 h-4 mr-2" />Save Bank Details</Button>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
